import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Upload, Pencil } from 'lucide-react';
import { useAlbums, useCreateAlbum, useDeleteAlbum } from '../../lib/queries';

export default function ManageAlbums() {
  const navigate = useNavigate();
  const { data: albums, isLoading } = useAlbums();
  const createMutation = useCreateAlbum();
  const deleteMutation = useDeleteAlbum();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    await createMutation.mutateAsync({ name: name.trim(), description: description.trim() || undefined });
    setName('');
    setDescription('');
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/admin')}
        className="flex items-center gap-2 text-ink-muted mb-4"
      >
        <ArrowLeft size={16} /> Tillbaka
      </button>

      <h1 className="text-3xl font-serif text-primary mb-6">Hantera album</h1>

      <form onSubmit={handleCreate} className="bg-white rounded-2xl p-6 shadow-sm mb-6 space-y-3">
        <h2 className="font-semibold">Skapa nytt album</h2>
        <label className="block">
          <span className="block text-sm font-medium mb-1">Namn</span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => { setName(e.target.value); }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium mb-1">Beskrivning (valfritt)</span>
          <textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); }}
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary focus:outline-none"
          />
        </label>
        <button
          type="submit"
          disabled={createMutation.isPending}
          className="bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <Plus size={16} /> Skapa
        </button>
      </form>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <h2 className="font-semibold p-4 border-b">Befintliga album ({albums?.length ?? 0})</h2>
        {isLoading ? (
          <p className="p-4 text-ink-muted">Laddar…</p>
        ) : (
          <ul className="divide-y">
            {albums?.map((album) => (
              <li key={album.id} className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{album.name}</p>
                  <p className="text-sm text-ink-muted truncate">
                    {album.photoCount} foton
                    {album.description ? ` · ${album.description}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/album/${album.id}/upload`}
                    className="p-2 rounded text-primary hover:bg-primary/10"
                    aria-label={`Ladda upp till ${album.name}`}
                  >
                    <Upload size={18} />
                  </Link>
                  <Link
                    to={`/admin/album/${album.id}/edit`}
                    className="p-2 rounded text-ink-muted hover:bg-gray-100"
                    aria-label={`Redigera ${album.name}`}
                  >
                    <Pencil size={18} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Radera "${album.name}" och alla dess foton?`)) {
                        deleteMutation.mutate(album.id);
                      }
                    }}
                    className="p-2 rounded text-red-600 hover:bg-red-50"
                    aria-label={`Radera ${album.name}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
